import {type RunHistoryKeyInfo} from '../../../types/run';

// maximum metrics to get when using regex to pick metrics
export const REGEX_METRIC_MAX_LEN = 100;

type RegexMetricSelection = {
  count: number;
  metrics: string[];
};

const EMPTY_SELECTION: RegexMetricSelection = {
  count: 0,
  metrics: [],
};

// Regex metric editing runs against history keys, which can be tens of
// thousands of strings. The input and the "N metrics matched" message both
// need results for the same regex, so cache the combined count + first page of
// metrics by the stable sorted key array and regex text. This keeps the normal
// edit path to one scan per regex instead of one scan for metrics and another
// scan for the count.
const selectionCache = new WeakMap<
  readonly string[],
  Map<string, RegexMetricSelection>
>();

export function getTruncatedSelectedMetrics(args: {
  metricRegex: string | undefined;
  keyInfo?: RunHistoryKeyInfo;
  sortedKeys?: string[];
}) {
  return getRegexMetricSelection(args).metrics;
}

function getMetricRegex(metricRegex: string | undefined) {
  if (metricRegex == null || metricRegex === '') {
    return null;
  }

  try {
    return new RegExp(metricRegex);
  } catch {
    return null;
  }
}

function getMetricKeys(keyInfo: RunHistoryKeyInfo, sortedKeys?: string[]) {
  if (sortedKeys != null && sortedKeys.length > 0) {
    return sortedKeys;
  }
  if (keyInfo.sortedKeys.length > 0) {
    return keyInfo.sortedKeys;
  }

  /**
   * KeyInfo can get big, into the 10,000s of keys.
   * At 20k keys running this .keys().sort() takes 10ms+ each time through
   * the fn, so prefer the server-provided sorted keys whenever they exist.
   */
  return Object.keys(keyInfo.keys).sort();
}

function getRegexMetricSelection({
  metricRegex,
  keyInfo,
  sortedKeys,
}: {
  metricRegex: string | undefined;
  keyInfo?: RunHistoryKeyInfo;
  sortedKeys?: string[];
}): RegexMetricSelection {
  if (keyInfo == null) {
    return EMPTY_SELECTION;
  }

  const regex = getMetricRegex(metricRegex);
  if (regex == null) {
    return EMPTY_SELECTION;
  }

  // Prefer the server-provided sortedKeys identity. Falling back to
  // Object.keys(...).sort() is correct but expensive enough to show up in
  // traces when repeated during regex editing.
  const keys = getMetricKeys(keyInfo, sortedKeys);
  const cacheKey = metricRegex ?? '';
  let cachedByRegex = selectionCache.get(keys);
  if (cachedByRegex == null) {
    cachedByRegex = new Map();
    selectionCache.set(keys, cachedByRegex);
  }

  const cached = cachedByRegex.get(cacheKey);
  if (cached != null) {
    return cached;
  }

  const metrics: string[] = [];
  let count = 0;
  for (const key of keys) {
    if (regex.test(key)) {
      count++;
      if (metrics.length < REGEX_METRIC_MAX_LEN) {
        metrics.push(key);
      }
    }
  }

  const selection = {count, metrics};
  cachedByRegex.set(cacheKey, selection);
  return selection;
}

export function getSelectedMetrics({
  metricRegex,
  keyInfo,
  sortedKeys,
  maxMatchedLimit,
}: {
  metricRegex: string | undefined;
  maxMatchedLimit?: number;
  keyInfo?: RunHistoryKeyInfo;
  sortedKeys?: string[];
}): string[] {
  if (maxMatchedLimit === REGEX_METRIC_MAX_LEN) {
    return getRegexMetricSelection({metricRegex, keyInfo, sortedKeys}).metrics;
  }

  if (keyInfo == null) {
    return [];
  }

  const regex = getMetricRegex(metricRegex);
  if (regex == null) {
    return [];
  }

  const keys = getMetricKeys(keyInfo, sortedKeys);

  const matchedKeys = [];
  for (const key of keys) {
    if (regex.test(key)) {
      matchedKeys.push(key);
    }
    if (maxMatchedLimit != null && matchedKeys.length === maxMatchedLimit) {
      break;
    }
  }
  return matchedKeys;
}

export function getSelectedMetricCount({
  metricRegex,
  keyInfo,
  sortedKeys,
}: {
  metricRegex: string | undefined;
  keyInfo?: RunHistoryKeyInfo;
  sortedKeys?: string[];
}): number {
  return getRegexMetricSelection({metricRegex, keyInfo, sortedKeys}).count;
}
